import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  canResendOhtMemberAccessEmail,
  coverageDiff,
  coverageSetsEqual,
  createSuperAdminOhtMember,
  deactivateSuperAdminOhtMember,
  formatCoverageConflictMessages,
  formatNavigatorIntakePrerequisiteMessages,
  getSuperAdminOht,
  listSuperAdminOhtMembers,
  memberAccessResultTone,
  memberProvisionSuccessMessage,
  memberRoleLabel,
  normalizeCanadianFsa,
  normalizeMemberEmail,
  reactivateSuperAdminOhtMember,
  resendSuperAdminOhtMemberAccessEmail,
  sortFsas,
  SuperAdminOhtApiError,
  updateSuperAdminOht,
  updateSuperAdminOhtCoverage,
  updateSuperAdminOhtNavigatorIntake,
  validateCanadianFsaInput,
  validateMemberDisplayName,
  validateMemberEmail,
  validateOhtCode,
  validateOhtName,
  type SuperAdminOhtDetail,
  type SuperAdminOhtMember,
  type SuperAdminOhtMemberRole,
} from "../../../../api/superAdminOhts";
import "./SuperAdminOhtDetailPage.css";

/**
 * Super Admin — OHT detail
 *
 * Identity/status, FSA coverage, team access, and Navigator intake governance.
 * Public intake availability enforcement is separate from this stored flag control.
 */

type LocationState = { ohtCreated?: boolean } | null;

type ConfirmKind = "activate" | "deactivate" | null;
type CoverageConfirmKind = "remove" | "remove-all" | null;
type MemberConfirmKind = "deactivate" | "reactivate" | null;
type IntakeConfirmKind = "enable" | "disable" | null;

export default function SuperAdminOhtDetailPage() {
  const nav = useNavigate();
  const location = useLocation();
  const params = useParams();
  const ohtIdNormalized = String(params.ohtId ?? "").trim();

  const [oht, setOht] = useState<SuperAdminOhtDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [resultTone, setResultTone] = useState<"success" | "warning">(
    "success"
  );

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editNameError, setEditNameError] = useState<string | null>(null);
  const [editCodeError, setEditCodeError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [intakeConfirmKind, setIntakeConfirmKind] =
    useState<IntakeConfirmKind>(null);
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [intakeError, setIntakeError] = useState<string | null>(null);
  const [intakeErrorMessages, setIntakeErrorMessages] = useState<string[]>(
    []
  );

  const [coverageEditing, setCoverageEditing] = useState(false);
  const [pendingFsas, setPendingFsas] = useState<string[]>([]);
  const [addFsaInput, setAddFsaInput] = useState("");
  const [addFsaError, setAddFsaError] = useState<string | null>(null);
  const [coverageErrorMessages, setCoverageErrorMessages] = useState<string[]>(
    []
  );
  const [coverageSaving, setCoverageSaving] = useState(false);
  const [coverageConfirm, setCoverageConfirm] =
    useState<CoverageConfirmKind>(null);

  const [members, setMembers] = useState<SuperAdminOhtMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [addingRole, setAddingRole] = useState<SuperAdminOhtMemberRole | null>(
    null
  );
  const [memberName, setMemberName] = useState("");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberNameError, setMemberNameError] = useState<string | null>(null);
  const [memberEmailError, setMemberEmailError] = useState<string | null>(null);
  const [memberFormError, setMemberFormError] = useState<string | null>(null);
  const [memberSaving, setMemberSaving] = useState(false);
  const [memberActionBusyId, setMemberActionBusyId] = useState<number | null>(
    null
  );
  const [memberResendBusyId, setMemberResendBusyId] = useState<number | null>(
    null
  );
  const [memberConfirm, setMemberConfirm] = useState<{
    kind: MemberConfirmKind;
    member: SuperAdminOhtMember;
  } | null>(null);

  function showOperationResult(
    message: string,
    tone: "success" | "warning" = "success"
  ) {
    setResultTone(tone);
    setSuccessMsg(message);
  }

  const savedFsas = useMemo(
    () =>
      sortFsas(
        (oht?.coverageFsas || []).map((row) => row.fsa).filter(Boolean)
      ),
    [oht]
  );

  const savedCreatedAtByFsa = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const row of oht?.coverageFsas || []) {
      const fsa = String(row.fsa || "").trim().toUpperCase();
      if (!fsa) continue;
      map.set(fsa, row.createdAt ?? null);
    }
    return map;
  }, [oht]);

  const pendingSorted = useMemo(() => sortFsas(pendingFsas), [pendingFsas]);
  const { toAdd, toRemove } = useMemo(
    () => coverageDiff(savedFsas, pendingSorted),
    [savedFsas, pendingSorted]
  );
  const coverageDirty = !coverageSetsEqual(savedFsas, pendingSorted);

  const ohtAdmins = useMemo(
    () => members.filter((m) => m.roleName === "OHTAdmin"),
    [members]
  );
  const communityNavigators = useMemo(
    () => members.filter((m) => m.roleName === "CommunityNavigator"),
    [members]
  );

  async function loadMembers(ohtId: string | number) {
    setMembersLoading(true);
    setMembersError(null);
    try {
      const rows = await listSuperAdminOhtMembers(ohtId);
      setMembers(rows);
    } catch (e: any) {
      setMembersError(e?.message || "Failed to load members.");
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }

  async function refreshOhtAndMembers(ohtId: string | number) {
    const [detail, rows] = await Promise.all([
      getSuperAdminOht(ohtId),
      listSuperAdminOhtMembers(ohtId),
    ]);
    setOht(detail);
    setEditName(detail.name || "");
    setEditCode(detail.code || "");
    setMembers(rows);
    setMembersError(null);
  }

  async function loadDetail() {
    if (!ohtIdNormalized) {
      setLoading(false);
      setNotFound(true);
      setErr("Ontario Health Team not found.");
      setOht(null);
      return;
    }

    setLoading(true);
    setErr(null);
    setNotFound(false);

    try {
      const detail = await getSuperAdminOht(ohtIdNormalized);
      setOht(detail);
      setEditName(detail.name || "");
      setEditCode(detail.code || "");
      await loadMembers(detail.ohtId);
    } catch (e: any) {
      if (e instanceof SuperAdminOhtApiError && e.status === 404) {
        setNotFound(true);
        setErr("Ontario Health Team not found.");
      } else {
        setErr(e?.message || "Failed to load OHT");
      }
      setOht(null);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!ohtIdNormalized) {
        if (!cancelled) {
          setLoading(false);
          setNotFound(true);
          setErr("Ontario Health Team not found.");
          setOht(null);
          setMembers([]);
        }
        return;
      }

      setLoading(true);
      setErr(null);
      setNotFound(false);
      setOht(null);
      setMembers([]);

      try {
        const detail = await getSuperAdminOht(ohtIdNormalized);
        if (cancelled) return;
        setOht(detail);
        setEditName(detail.name || "");
        setEditCode(detail.code || "");
        try {
          const rows = await listSuperAdminOhtMembers(detail.ohtId);
          if (!cancelled) {
            setMembers(rows);
            setMembersError(null);
          }
        } catch (memberErr: any) {
          if (!cancelled) {
            setMembersError(memberErr?.message || "Failed to load members.");
            setMembers([]);
          }
        }
      } catch (e: any) {
        if (cancelled) return;
        if (e instanceof SuperAdminOhtApiError && e.status === 404) {
          setNotFound(true);
          setErr("Ontario Health Team not found.");
        } else {
          setErr(e?.message || "Failed to load OHT");
        }
        setOht(null);
        setMembers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [ohtIdNormalized]);

  useEffect(() => {
    const state = (location.state as LocationState) || null;
    if (state?.ohtCreated) {
      showOperationResult("Ontario Health Team created.");
      nav(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, nav]);

  function backToOhts() {
    nav("/portal/super-admin/ohts");
  }

  function startEdit() {
    if (!oht || coverageEditing || coverageSaving || memberSaving || addingRole) {
      return;
    }
    setEditName(oht.name || "");
    setEditCode(oht.code || "");
    setEditNameError(null);
    setEditCodeError(null);
    setEditError(null);
    setEditing(true);
  }

  function cancelEdit() {
    if (!oht) return;
    setEditName(oht.name || "");
    setEditCode(oht.code || "");
    setEditNameError(null);
    setEditCodeError(null);
    setEditError(null);
    setEditing(false);
  }

  async function saveEdit() {
    if (!oht || saving) return;

    const nextNameError = validateOhtName(editName);
    const nextCodeError = validateOhtCode(editCode);
    setEditNameError(nextNameError);
    setEditCodeError(nextCodeError);
    setEditError(null);
    if (nextNameError || nextCodeError) return;

    setSaving(true);
    try {
      const updated = await updateSuperAdminOht(oht.ohtId, {
        name: editName.trim(),
        code: editCode.trim(),
      });
      setOht(updated);
      setEditName(updated.name || "");
      setEditCode(updated.code || "");
      setEditing(false);
      showOperationResult("OHT details updated.");
    } catch (e: any) {
      if (e instanceof SuperAdminOhtApiError && e.status === 409) {
        setEditCodeError(
          e.message ||
            "An Ontario Health Team with this code already exists."
        );
      } else {
        setEditError(e?.message || "Failed to update OHT.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function confirmStatusChange() {
    if (!oht || !confirmKind || statusBusy) return;

    const nextActive = confirmKind === "activate";
    setStatusBusy(true);
    setStatusError(null);

    try {
      const updated = await updateSuperAdminOht(oht.ohtId, {
        isActive: nextActive,
      });
      setOht(updated);
      setEditName(updated.name || "");
      setEditCode(updated.code || "");
      setConfirmKind(null);
      showOperationResult(
        nextActive ? "OHT activated." : "OHT deactivated."
      );
    } catch (e: any) {
      setStatusError(e?.message || "Failed to update OHT status.");
    } finally {
      setStatusBusy(false);
    }
  }

  async function confirmIntakeChange() {
    if (!oht || !intakeConfirmKind || intakeBusy) return;

    const enabled = intakeConfirmKind === "enable";
    setIntakeBusy(true);
    setIntakeError(null);
    setIntakeErrorMessages([]);
    setSuccessMsg(null);

    try {
      const updated = await updateSuperAdminOhtNavigatorIntake(
        oht.ohtId,
        enabled
      );
      setOht(updated);
      setEditName(updated.name || "");
      setEditCode(updated.code || "");
      setIntakeConfirmKind(null);
      showOperationResult(
        enabled ? "Navigator intake enabled." : "Navigator intake disabled."
      );
    } catch (e: any) {
      const readiness =
        e instanceof SuperAdminOhtApiError
          ? (e.body as { readiness?: any } | null | undefined)?.readiness
          : null;
      const messages = formatNavigatorIntakePrerequisiteMessages(readiness);
      setIntakeErrorMessages(messages);
      setIntakeError(
        e?.message ||
          (enabled
            ? "Navigator intake could not be enabled."
            : "Navigator intake could not be disabled.")
      );
      setIntakeConfirmKind(null);
      try {
        const refreshed = await getSuperAdminOht(oht.ohtId);
        setOht(refreshed);
      } catch {
        // Keep prior OHT state if refresh fails.
      }
    } finally {
      setIntakeBusy(false);
    }
  }

  function startCoverageEdit() {
    if (
      !oht ||
      editing ||
      saving ||
      statusBusy ||
      coverageSaving ||
      memberSaving ||
      addingRole
    ) {
      return;
    }
    setPendingFsas(savedFsas);
    setAddFsaInput("");
    setAddFsaError(null);
    setCoverageErrorMessages([]);
    setCoverageConfirm(null);
    setCoverageEditing(true);
  }

  function cancelCoverageEdit() {
    setPendingFsas(savedFsas);
    setAddFsaInput("");
    setAddFsaError(null);
    setCoverageErrorMessages([]);
    setCoverageConfirm(null);
    setCoverageEditing(false);
  }

  function addPendingFsa() {
    if (coverageSaving) return;
    const error = validateCanadianFsaInput(addFsaInput, pendingFsas);
    if (error) {
      setAddFsaError(error);
      return;
    }
    const normalized = normalizeCanadianFsa(addFsaInput);
    if (!normalized) {
      setAddFsaError(
        "Enter a valid Canadian FSA (letter-digit-letter, for example L5L)."
      );
      return;
    }
    setPendingFsas(sortFsas([...pendingFsas, normalized]));
    setAddFsaInput("");
    setAddFsaError(null);
    setCoverageErrorMessages([]);
  }

  function removePendingFsa(fsa: string) {
    if (coverageSaving) return;
    const target = String(fsa).trim().toUpperCase();
    setPendingFsas(pendingFsas.filter((item) => item !== target));
    setCoverageErrorMessages([]);
  }

  function requestCoverageSave() {
    if (!oht || coverageSaving || !coverageDirty) return;
    setCoverageErrorMessages([]);

    if (pendingSorted.length === 0 && savedFsas.length > 0) {
      setCoverageConfirm("remove-all");
      return;
    }
    if (toRemove.length > 0) {
      setCoverageConfirm("remove");
      return;
    }
    void performCoverageSave();
  }

  async function performCoverageSave() {
    if (!oht || coverageSaving) return;

    setCoverageSaving(true);
    setCoverageConfirm(null);
    setCoverageErrorMessages([]);
    setSuccessMsg(null);

    try {
      const updated = await updateSuperAdminOhtCoverage(
        oht.ohtId,
        pendingSorted
      );
      setOht(updated);
      setEditName(updated.name || "");
      setEditCode(updated.code || "");
      setPendingFsas(
        sortFsas(
          (updated.coverageFsas || []).map((row) => row.fsa).filter(Boolean)
        )
      );
      setAddFsaInput("");
      setAddFsaError(null);
      setCoverageEditing(false);
      showOperationResult("OHT coverage updated.");
    } catch (e: any) {
      if (e instanceof SuperAdminOhtApiError && e.status === 409) {
        const messages = formatCoverageConflictMessages(e.body);
        setCoverageErrorMessages(
          messages.length ? messages : [e.message || "Coverage could not be updated."]
        );
      } else {
        setCoverageErrorMessages([
          e?.message || "Failed to update coverage.",
        ]);
      }
    } finally {
      setCoverageSaving(false);
    }
  }

  function openAddMember(roleName: SuperAdminOhtMemberRole) {
    if (
      editing ||
      coverageEditing ||
      saving ||
      statusBusy ||
      coverageSaving ||
      memberSaving ||
      memberActionBusyId != null
    ) {
      return;
    }
    setAddingRole(roleName);
    setMemberName("");
    setMemberEmail("");
    setMemberNameError(null);
    setMemberEmailError(null);
    setMemberFormError(null);
  }

  function cancelAddMember() {
    setAddingRole(null);
    setMemberName("");
    setMemberEmail("");
    setMemberNameError(null);
    setMemberEmailError(null);
    setMemberFormError(null);
  }

  async function submitAddMember() {
    if (!oht || !addingRole || memberSaving) return;

    const nameError = validateMemberDisplayName(memberName);
    const emailError = validateMemberEmail(memberEmail);
    setMemberNameError(nameError);
    setMemberEmailError(emailError);
    setMemberFormError(null);
    if (nameError || emailError) return;

    setMemberSaving(true);
    setSuccessMsg(null);
    try {
      const result = await createSuperAdminOhtMember(oht.ohtId, {
        displayName: memberName.trim(),
        email: normalizeMemberEmail(memberEmail) || "",
        roleName: addingRole,
      });
      await refreshOhtAndMembers(oht.ohtId);
      cancelAddMember();
      showOperationResult(
        memberProvisionSuccessMessage({
          ...result,
          email: result.member.email,
        }),
        memberAccessResultTone(result)
      );
    } catch (e: any) {
      setMemberFormError(e?.message || "Failed to add member.");
    } finally {
      setMemberSaving(false);
    }
  }

  async function confirmMemberAction() {
    if (!oht || !memberConfirm || memberActionBusyId != null) return;

    const { kind, member } = memberConfirm;
    setMemberActionBusyId(member.userRoleId);
    setMembersError(null);
    setSuccessMsg(null);

    try {
      if (kind === "deactivate") {
        await deactivateSuperAdminOhtMember(oht.ohtId, member.userRoleId);
        showOperationResult("Assignment deactivated.");
      } else {
        const result = await reactivateSuperAdminOhtMember(
          oht.ohtId,
          member.userRoleId
        );
        if (result.alreadyActive) {
          showOperationResult("This assignment is already active.");
        } else {
          showOperationResult(
            memberProvisionSuccessMessage({
              assignmentState: "reactivated",
              notification: result.notification,
              warning: result.warning,
              email: result.member.email || member.email,
            }),
            memberAccessResultTone({
              assignmentState: "reactivated",
              notification: result.notification,
            })
          );
        }
      }
      setMemberConfirm(null);
      await refreshOhtAndMembers(oht.ohtId);
    } catch (e: any) {
      setMembersError(e?.message || "Failed to update assignment.");
      setMemberConfirm(null);
    } finally {
      setMemberActionBusyId(null);
    }
  }

  async function resendMemberAccessEmail(member: SuperAdminOhtMember) {
    if (!oht || memberResendBusyId != null) return;
    if (
      !canResendOhtMemberAccessEmail({
        member,
        ohtIsActive: oht.isActive === true,
      })
    ) {
      return;
    }

    setMemberResendBusyId(member.userRoleId);
    setMembersError(null);
    setSuccessMsg(null);

    try {
      await resendSuperAdminOhtMemberAccessEmail(oht.ohtId, member.userRoleId);
      const email =
        normalizeMemberEmail(member.email) || member.email || "the member";
      showOperationResult(`Access email sent to ${email}.`);
    } catch (e: any) {
      const message =
        e instanceof SuperAdminOhtApiError
          ? e.message
          : e?.message || "The access email could not be sent. Please try again.";
      setMembersError(message);
    } finally {
      setMemberResendBusyId(null);
    }
  }

  if (loading) {
    return (
      <div className="sa-oht-detail">
        <div className="sa-oht-detail__loading">Loading OHT...</div>
      </div>
    );
  }

  if (notFound || (!oht && err)) {
    return (
      <div className="sa-oht-detail">
        <div className="sa-oht-detail__topbar">
          <div className="sa-oht-detail__titleWrap">
            <div className="sa-oht-detail__eyebrow">OHT governance</div>
            <h1 className="sa-oht-detail__title">Ontario Health Team</h1>
          </div>
          <div className="sa-oht-detail__actions">
            <button
              type="button"
              className="sa-oht-detail__btn"
              onClick={backToOhts}
            >
              ← Back to OHTs
            </button>
          </div>
        </div>
        <div className="sa-oht-detail__error" role="alert">
          {err || "Ontario Health Team not found."}
        </div>
      </div>
    );
  }

  if (!oht) {
    return (
      <div className="sa-oht-detail">
        <div className="sa-oht-detail__error" role="alert">
          Ontario Health Team not found.
        </div>
      </div>
    );
  }

  const readiness = oht.navigatorIntakeReadiness || {
    hasCoverage: false,
    hasActiveNavigator: false,
    canEnable: false,
  };

  const coverageFsas = Array.isArray(oht.coverageFsas)
    ? [...oht.coverageFsas].sort((a, b) =>
        String(a.fsa || "").localeCompare(String(b.fsa || ""))
      )
    : [];
  const displayName = oht.name || "this OHT";
  const busy =
    saving ||
    statusBusy ||
    intakeBusy ||
    coverageSaving ||
    memberSaving ||
    memberActionBusyId != null ||
    memberResendBusyId != null;

  let overallLabel = "Needs setup";
  let overallClass = "sa-oht-detail__overall";
  if (oht.isNavigatorIntakeEnabled) {
    overallLabel = "Navigator intake enabled";
    overallClass = "sa-oht-detail__overall sa-oht-detail__overall--enabled";
  } else if (readiness.canEnable) {
    overallLabel = "Ready to enable intake";
    overallClass = "sa-oht-detail__overall sa-oht-detail__overall--ready";
  }

  const pendingSummaryParts: string[] = [];
  if (toAdd.length) {
    pendingSummaryParts.push(
      `${toAdd.length} FSA${toAdd.length === 1 ? "" : "s"} will be added`
    );
  }
  if (toRemove.length) {
    pendingSummaryParts.push(
      `${toRemove.length} FSA${toRemove.length === 1 ? "" : "s"} will be removed`
    );
  }

  return (
    <div className="sa-oht-detail">
      <div className="sa-oht-detail__topbar">
        <div className="sa-oht-detail__titleWrap">
          <div className="sa-oht-detail__eyebrow">OHT governance</div>
          <div className="sa-oht-detail__titleRow">
            <h1 className="sa-oht-detail__title">{oht.name || "—"}</h1>
            <span
              className={
                oht.isActive
                  ? "sa-oht-detail__status sa-oht-detail__status--active"
                  : "sa-oht-detail__status sa-oht-detail__status--inactive"
              }
            >
              {oht.isActive ? "Active" : "Inactive"}
            </span>
            <span
              className={
                oht.isNavigatorIntakeEnabled
                  ? "sa-oht-detail__status sa-oht-detail__status--enabled"
                  : "sa-oht-detail__status sa-oht-detail__status--disabled"
              }
            >
              Intake {oht.isNavigatorIntakeEnabled ? "enabled" : "disabled"}
            </span>
          </div>
          <p className="sa-oht-detail__subtitle">
            Code: {oht.code || "—"} · OHT ID: {oht.ohtId}
          </p>
        </div>

        <div className="sa-oht-detail__actions">
          <button
            type="button"
            className="sa-oht-detail__btn"
            onClick={backToOhts}
            disabled={busy}
          >
            ← Back to OHTs
          </button>
          <button
            type="button"
            className="sa-oht-detail__btn"
            onClick={() => {
              void loadDetail();
            }}
            disabled={busy}
          >
            Refresh
          </button>
        </div>
      </div>

      {successMsg ? (
        <div
          className={
            resultTone === "warning"
              ? "sa-oht-detail__warning"
              : "sa-oht-detail__success"
          }
          role="status"
        >
          {successMsg}
        </div>
      ) : null}

      {err ? (
        <div className="sa-oht-detail__error" role="alert">
          {err}
        </div>
      ) : null}

      {statusError ? (
        <div className="sa-oht-detail__error" role="alert">
          {statusError}
        </div>
      ) : null}

      <section className="sa-oht-detail__kpis" aria-label="OHT counts">
        <Kpi label="FSA coverage" value={oht.coverageFsaCount} />
        <Kpi label="Approved clinics" value={oht.clinicCount} />
        <Kpi label="Family doctors" value={oht.doctorCount} />
        <Kpi label="OHT Admins" value={oht.activeOhtAdminCount} />
        <Kpi
          label="Community Navigators"
          value={oht.activeCommunityNavigatorCount}
        />
      </section>

      <div className="sa-oht-detail__grid">
        <section className="sa-oht-detail__card" aria-labelledby="oht-identity">
          <div className="sa-oht-detail__cardHeader">
            <h2 id="oht-identity" className="sa-oht-detail__cardTitle">
              Identity
            </h2>
            {!editing ? (
              <button
                type="button"
                className="sa-oht-detail__btn"
                onClick={startEdit}
                disabled={busy || coverageEditing || !!addingRole}
              >
                Edit OHT
              </button>
            ) : null}
          </div>

          {editing ? (
            <div className="sa-oht-detail__editForm">
              {editError ? (
                <div className="sa-oht-detail__error" role="alert">
                  {editError}
                </div>
              ) : null}

              <div className="sa-oht-detail__field">
                <label
                  className="sa-oht-detail__fieldLabel"
                  htmlFor="oht-edit-name"
                >
                  OHT name
                </label>
                <input
                  id="oht-edit-name"
                  type="text"
                  className="sa-oht-detail__input"
                  value={editName}
                  maxLength={255}
                  disabled={saving}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    if (editNameError) setEditNameError(null);
                  }}
                />
                {editNameError ? (
                  <div className="sa-oht-detail__fieldError" role="alert">
                    {editNameError}
                  </div>
                ) : null}
              </div>

              <div className="sa-oht-detail__field">
                <label
                  className="sa-oht-detail__fieldLabel"
                  htmlFor="oht-edit-code"
                >
                  OHT code
                </label>
                <input
                  id="oht-edit-code"
                  type="text"
                  className="sa-oht-detail__input"
                  value={editCode}
                  maxLength={50}
                  disabled={saving}
                  onChange={(e) => {
                    setEditCode(e.target.value);
                    if (editCodeError) setEditCodeError(null);
                  }}
                />
                {editCodeError ? (
                  <div className="sa-oht-detail__fieldError" role="alert">
                    {editCodeError}
                  </div>
                ) : null}
              </div>

              <div className="sa-oht-detail__btnRow">
                <button
                  type="button"
                  className="sa-oht-detail__btn"
                  onClick={cancelEdit}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="sa-oht-detail__btn sa-oht-detail__btn--primary"
                  onClick={() => {
                    void saveEdit();
                  }}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          ) : (
            <dl className="sa-oht-detail__dl">
              <div className="sa-oht-detail__row">
                <dt className="sa-oht-detail__dt">OHT Name</dt>
                <dd className="sa-oht-detail__dd">{oht.name || "—"}</dd>
              </div>
              <div className="sa-oht-detail__row">
                <dt className="sa-oht-detail__dt">Code</dt>
                <dd className="sa-oht-detail__dd">{oht.code || "—"}</dd>
              </div>
              <div className="sa-oht-detail__row">
                <dt className="sa-oht-detail__dt">Created</dt>
                <dd className="sa-oht-detail__dd">{fmtDate(oht.createdAt)}</dd>
              </div>
              <div className="sa-oht-detail__row">
                <dt className="sa-oht-detail__dt">Updated</dt>
                <dd className="sa-oht-detail__dd">{fmtDate(oht.updatedAt)}</dd>
              </div>
            </dl>
          )}
        </section>

        <section className="sa-oht-detail__card" aria-labelledby="oht-status">
          <h2 id="oht-status" className="sa-oht-detail__cardTitle">
            Status
          </h2>
          <dl className="sa-oht-detail__dl">
            <div className="sa-oht-detail__row">
              <dt className="sa-oht-detail__dt">Organization status</dt>
              <dd className="sa-oht-detail__dd">
                <span
                  className={
                    oht.isActive
                      ? "sa-oht-detail__status sa-oht-detail__status--active"
                      : "sa-oht-detail__status sa-oht-detail__status--inactive"
                  }
                >
                  {oht.isActive ? "Active" : "Inactive"}
                </span>
              </dd>
            </div>
            <div className="sa-oht-detail__row">
              <dt className="sa-oht-detail__dt">Navigator intake status</dt>
              <dd className="sa-oht-detail__dd">
                <span
                  className={
                    oht.isNavigatorIntakeEnabled
                      ? "sa-oht-detail__status sa-oht-detail__status--enabled"
                      : "sa-oht-detail__status sa-oht-detail__status--disabled"
                  }
                >
                  {oht.isNavigatorIntakeEnabled ? "Enabled" : "Disabled"}
                </span>
              </dd>
            </div>
          </dl>

          <div className="sa-oht-detail__btnRow">
            {oht.isActive ? (
              <button
                type="button"
                className="sa-oht-detail__btn sa-oht-detail__btn--danger"
                onClick={() => {
                  setStatusError(null);
                  setConfirmKind("deactivate");
                }}
                disabled={busy || editing || coverageEditing || !!addingRole}
              >
                Deactivate OHT
              </button>
            ) : (
              <button
                type="button"
                className="sa-oht-detail__btn sa-oht-detail__btn--primary"
                onClick={() => {
                  setStatusError(null);
                  setConfirmKind("activate");
                }}
                disabled={busy || editing || coverageEditing || !!addingRole}
              >
                Activate OHT
              </button>
            )}
          </div>
        </section>

        <section
          className="sa-oht-detail__card"
          aria-labelledby="oht-readiness"
        >
          <div className="sa-oht-detail__cardHeader">
            <h2 id="oht-readiness" className="sa-oht-detail__cardTitle">
              Navigator intake readiness
            </h2>
            {oht.isNavigatorIntakeEnabled ? (
              <button
                type="button"
                className="sa-oht-detail__btn sa-oht-detail__btn--danger"
                onClick={() => {
                  setIntakeError(null);
                  setIntakeErrorMessages([]);
                  setIntakeConfirmKind("disable");
                }}
                disabled={
                  busy || editing || coverageEditing || !!addingRole
                }
              >
                Disable Navigator intake
              </button>
            ) : readiness.canEnable ? (
              <button
                type="button"
                className="sa-oht-detail__btn sa-oht-detail__btn--primary"
                onClick={() => {
                  setIntakeError(null);
                  setIntakeErrorMessages([]);
                  setIntakeConfirmKind("enable");
                }}
                disabled={
                  busy || editing || coverageEditing || !!addingRole
                }
              >
                Enable Navigator intake
              </button>
            ) : (
              <button
                type="button"
                className="sa-oht-detail__btn"
                disabled
                title="Complete the missing requirements before enabling intake."
              >
                Enable Navigator intake
              </button>
            )}
          </div>

          {!oht.isNavigatorIntakeEnabled && !readiness.canEnable ? (
            <p className="sa-oht-detail__helper" role="note">
              Complete the missing requirements before enabling intake.
            </p>
          ) : null}

          {intakeError || intakeErrorMessages.length ? (
            <div className="sa-oht-detail__error" role="alert">
              {intakeError ? <div>{intakeError}</div> : null}
              {intakeErrorMessages.length ? (
                <ul className="sa-oht-detail__errorList">
                  {intakeErrorMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          <ul className="sa-oht-detail__prereqList">
            <Prereq
              label="OHT active"
              pass={oht.isActive === true}
              passText="Pass"
              failText="Missing"
            />
            <Prereq
              label="Coverage configured"
              pass={readiness.hasCoverage === true}
              passText="Pass"
              failText="Missing"
            />
            <Prereq
              label="Active Community Navigator"
              pass={readiness.hasActiveNavigator === true}
              passText="Pass"
              failText="Missing"
            />
          </ul>
          <div className={overallClass} role="status">
            {overallLabel}
          </div>
        </section>
      </div>

      <section className="sa-oht-detail__card" aria-labelledby="oht-coverage">
        <div className="sa-oht-detail__cardHeader">
          <h2 id="oht-coverage" className="sa-oht-detail__cardTitle">
            Coverage FSAs
          </h2>
          {!coverageEditing ? (
            <button
              type="button"
              className="sa-oht-detail__btn"
              onClick={startCoverageEdit}
              disabled={busy || editing || !!addingRole}
            >
              Edit coverage
            </button>
          ) : null}
        </div>

        {coverageEditing ? (
          <div className="sa-oht-detail__coverageEdit">
            {coverageErrorMessages.length ? (
              <div className="sa-oht-detail__error" role="alert">
                <ul className="sa-oht-detail__errorList">
                  {coverageErrorMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {pendingSummaryParts.length ? (
              <p className="sa-oht-detail__pendingSummary" role="status">
                {pendingSummaryParts.join(" · ")}
              </p>
            ) : (
              <p className="sa-oht-detail__muted">No pending coverage changes.</p>
            )}

            <div className="sa-oht-detail__addFsaRow">
              <div className="sa-oht-detail__field sa-oht-detail__field--grow">
                <label
                  className="sa-oht-detail__fieldLabel"
                  htmlFor="oht-add-fsa"
                >
                  Add FSA
                </label>
                <input
                  id="oht-add-fsa"
                  type="text"
                  className="sa-oht-detail__input"
                  placeholder="L5L"
                  value={addFsaInput}
                  maxLength={8}
                  disabled={coverageSaving}
                  onChange={(e) => {
                    setAddFsaInput(e.target.value);
                    if (addFsaError) setAddFsaError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addPendingFsa();
                    }
                  }}
                />
                {addFsaError ? (
                  <div className="sa-oht-detail__fieldError" role="alert">
                    {addFsaError}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                className="sa-oht-detail__btn"
                onClick={addPendingFsa}
                disabled={coverageSaving}
              >
                Add FSA
              </button>
            </div>

            {pendingSorted.length === 0 ? (
              <p className="sa-oht-detail__muted">
                No FSAs in the pending coverage set.
              </p>
            ) : (
              <div className="sa-oht-detail__coverageWrap">
                <table className="sa-oht-detail__table">
                  <thead>
                    <tr>
                      <th className="sa-oht-detail__th" scope="col">
                        FSA
                      </th>
                      <th className="sa-oht-detail__th" scope="col">
                        Added date
                      </th>
                      <th className="sa-oht-detail__th" scope="col">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingSorted.map((fsa) => {
                      const isNew = toAdd.includes(fsa);
                      return (
                        <tr
                          key={fsa}
                          className={
                            isNew ? "sa-oht-detail__row--pendingAdd" : undefined
                          }
                        >
                          <td className="sa-oht-detail__td">
                            <span className="sa-oht-detail__fsaCode">{fsa}</span>
                            {isNew ? (
                              <span className="sa-oht-detail__pendingBadge">
                                New
                              </span>
                            ) : null}
                          </td>
                          <td
                            className={
                              isNew
                                ? "sa-oht-detail__td sa-oht-detail__td--muted"
                                : "sa-oht-detail__td"
                            }
                          >
                            {isNew
                              ? "—"
                              : fmtDate(savedCreatedAtByFsa.get(fsa))}
                          </td>
                          <td className="sa-oht-detail__td">
                            <button
                              type="button"
                              className="sa-oht-detail__btn sa-oht-detail__btn--compact"
                              onClick={() => removePendingFsa(fsa)}
                              disabled={coverageSaving}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="sa-oht-detail__btnRow">
              <button
                type="button"
                className="sa-oht-detail__btn"
                onClick={cancelCoverageEdit}
                disabled={coverageSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sa-oht-detail__btn sa-oht-detail__btn--primary"
                onClick={requestCoverageSave}
                disabled={coverageSaving || !coverageDirty}
              >
                {coverageSaving ? "Saving..." : "Save coverage"}
              </button>
            </div>
          </div>
        ) : coverageFsas.length === 0 ? (
          <p className="sa-oht-detail__muted">No FSA coverage configured.</p>
        ) : (
          <div className="sa-oht-detail__coverageWrap">
            <table className="sa-oht-detail__table">
              <thead>
                <tr>
                  <th className="sa-oht-detail__th" scope="col">
                    FSA
                  </th>
                  <th className="sa-oht-detail__th" scope="col">
                    Added date
                  </th>
                </tr>
              </thead>
              <tbody>
                {coverageFsas.map((item) => (
                  <tr key={item.fsa}>
                    <td className="sa-oht-detail__td">{item.fsa || "—"}</td>
                    <td className="sa-oht-detail__td">
                      {fmtDate(item.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="sa-oht-detail__card" aria-labelledby="oht-team">
        <div className="sa-oht-detail__cardHeader">
          <h2 id="oht-team" className="sa-oht-detail__cardTitle">
            Team access
          </h2>
        </div>

        {!oht.isActive ? (
          <p className="sa-oht-detail__helper" role="note">
            Assignments can be configured while this OHT is inactive. Portal
            access requires the OHT to be active. Access emails can be sent when
            the OHT is active.
          </p>
        ) : null}

        {membersError ? (
          <div className="sa-oht-detail__error" role="alert">
            {membersError}
          </div>
        ) : null}

        {addingRole ? (
          <div className="sa-oht-detail__editForm sa-oht-detail__memberForm">
            <h3 className="sa-oht-detail__subTitle">
              Add {memberRoleLabel(addingRole)}
            </h3>
            {memberFormError ? (
              <div className="sa-oht-detail__error" role="alert">
                {memberFormError}
              </div>
            ) : null}
            <div className="sa-oht-detail__field">
              <span className="sa-oht-detail__fieldLabel">Role</span>
              <div className="sa-oht-detail__readonlyValue">
                {memberRoleLabel(addingRole)}
              </div>
            </div>
            <div className="sa-oht-detail__field">
              <label
                className="sa-oht-detail__fieldLabel"
                htmlFor="oht-member-name"
              >
                Full name
              </label>
              <input
                id="oht-member-name"
                type="text"
                className="sa-oht-detail__input"
                value={memberName}
                maxLength={255}
                disabled={memberSaving}
                onChange={(e) => {
                  setMemberName(e.target.value);
                  if (memberNameError) setMemberNameError(null);
                }}
              />
              {memberNameError ? (
                <div className="sa-oht-detail__fieldError" role="alert">
                  {memberNameError}
                </div>
              ) : null}
            </div>
            <div className="sa-oht-detail__field">
              <label
                className="sa-oht-detail__fieldLabel"
                htmlFor="oht-member-email"
              >
                Email
              </label>
              <input
                id="oht-member-email"
                type="email"
                className="sa-oht-detail__input"
                value={memberEmail}
                maxLength={255}
                disabled={memberSaving}
                onChange={(e) => {
                  setMemberEmail(e.target.value);
                  if (memberEmailError) setMemberEmailError(null);
                }}
              />
              {memberEmailError ? (
                <div className="sa-oht-detail__fieldError" role="alert">
                  {memberEmailError}
                </div>
              ) : null}
            </div>
            <div className="sa-oht-detail__btnRow">
              <button
                type="button"
                className="sa-oht-detail__btn"
                onClick={cancelAddMember}
                disabled={memberSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sa-oht-detail__btn sa-oht-detail__btn--primary"
                onClick={() => {
                  void submitAddMember();
                }}
                disabled={memberSaving}
              >
                {memberSaving ? "Adding..." : `Add ${memberRoleLabel(addingRole)}`}
              </button>
            </div>
          </div>
        ) : null}

        <div className="sa-oht-detail__memberGroups">
          <MemberRoleGroup
            title="OHT Admins"
            emptyText="No OHT Admins assigned."
            members={ohtAdmins}
            loading={membersLoading}
            busy={busy}
            ohtIsActive={oht.isActive === true}
            actionBusyId={memberActionBusyId}
            resendBusyId={memberResendBusyId}
            showAdd={!addingRole}
            addLabel="Add OHT Admin"
            onAdd={() => openAddMember("OHTAdmin")}
            onDeactivate={(member) =>
              setMemberConfirm({ kind: "deactivate", member })
            }
            onReactivate={(member) =>
              setMemberConfirm({ kind: "reactivate", member })
            }
            onResend={(member) => {
              void resendMemberAccessEmail(member);
            }}
          />
          <MemberRoleGroup
            title="Community Navigators"
            emptyText="No Community Navigators assigned."
            members={communityNavigators}
            loading={membersLoading}
            busy={busy}
            ohtIsActive={oht.isActive === true}
            actionBusyId={memberActionBusyId}
            resendBusyId={memberResendBusyId}
            showAdd={!addingRole}
            addLabel="Add Community Navigator"
            onAdd={() => openAddMember("CommunityNavigator")}
            onDeactivate={(member) =>
              setMemberConfirm({ kind: "deactivate", member })
            }
            onReactivate={(member) =>
              setMemberConfirm({ kind: "reactivate", member })
            }
            onResend={(member) => {
              void resendMemberAccessEmail(member);
            }}
          />
        </div>
      </section>

      {confirmKind ? (
        <div
          className="sa-oht-detail__modalOverlay"
          role="presentation"
          onClick={() => {
            if (!statusBusy) setConfirmKind(null);
          }}
        >
          <div
            className="sa-oht-detail__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="oht-status-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="oht-status-confirm-title"
              className="sa-oht-detail__modalTitle"
            >
              {confirmKind === "deactivate"
                ? `Deactivate ${displayName}?`
                : `Activate ${displayName}?`}
            </h2>
            <p className="sa-oht-detail__modalBody">
              {confirmKind === "deactivate"
                ? "This will prevent OHT Admins and Community Navigators from accessing this OHT. Public Navigator intake will also be disabled. Existing coverage, users and historical data will remain in place."
                : "This restores internal OHT access for active assignments. Navigator intake will remain disabled until it is enabled separately."}
            </p>
            <div className="sa-oht-detail__btnRow sa-oht-detail__btnRow--end">
              <button
                type="button"
                className="sa-oht-detail__btn"
                onClick={() => setConfirmKind(null)}
                disabled={statusBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  confirmKind === "deactivate"
                    ? "sa-oht-detail__btn sa-oht-detail__btn--danger"
                    : "sa-oht-detail__btn sa-oht-detail__btn--primary"
                }
                onClick={() => {
                  void confirmStatusChange();
                }}
                disabled={statusBusy}
              >
                {statusBusy
                  ? "Working…"
                  : confirmKind === "deactivate"
                    ? "Deactivate OHT"
                    : "Activate OHT"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {intakeConfirmKind ? (
        <div
          className="sa-oht-detail__modalOverlay"
          role="presentation"
          onClick={() => {
            if (!intakeBusy) setIntakeConfirmKind(null);
          }}
        >
          <div
            className="sa-oht-detail__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="oht-intake-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="oht-intake-confirm-title"
              className="sa-oht-detail__modalTitle"
            >
              {intakeConfirmKind === "enable"
                ? "Enable Navigator intake?"
                : "Disable Navigator intake?"}
            </h2>
            <p className="sa-oht-detail__modalBody">
              {intakeConfirmKind === "enable"
                ? "Patients in this OHT’s covered FSAs will be able to submit Community Navigator requests once public intake is enabled. Confirm that the OHT has appropriate coverage and Navigator staffing before continuing."
                : "This will stop new public Community Navigator requests for this OHT. Existing Navigator cases and historical data will remain unchanged."}
            </p>
            <div className="sa-oht-detail__btnRow sa-oht-detail__btnRow--end">
              <button
                type="button"
                className="sa-oht-detail__btn"
                onClick={() => setIntakeConfirmKind(null)}
                disabled={intakeBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  intakeConfirmKind === "disable"
                    ? "sa-oht-detail__btn sa-oht-detail__btn--danger"
                    : "sa-oht-detail__btn sa-oht-detail__btn--primary"
                }
                onClick={() => {
                  void confirmIntakeChange();
                }}
                disabled={intakeBusy}
              >
                {intakeBusy
                  ? intakeConfirmKind === "enable"
                    ? "Enabling..."
                    : "Disabling..."
                  : intakeConfirmKind === "enable"
                    ? "Enable Navigator intake"
                    : "Disable Navigator intake"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {coverageConfirm ? (
        <div
          className="sa-oht-detail__modalOverlay"
          role="presentation"
          onClick={() => {
            if (!coverageSaving) setCoverageConfirm(null);
          }}
        >
          <div
            className="sa-oht-detail__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="oht-coverage-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="oht-coverage-confirm-title"
              className="sa-oht-detail__modalTitle"
            >
              {coverageConfirm === "remove-all"
                ? "Remove all FSA coverage?"
                : "Update OHT coverage?"}
            </h2>
            <p className="sa-oht-detail__modalBody">
              {coverageConfirm === "remove-all"
                ? "This OHT will no longer have geographic coverage. Navigator intake will be disabled automatically. Existing users and historical data will remain in place."
                : "Removing FSA coverage may change which clinics and Community Navigator cases belong to this OHT. The system will block removal when active Navigator cases or OHT targets still depend on an FSA."}
            </p>
            <div className="sa-oht-detail__btnRow sa-oht-detail__btnRow--end">
              <button
                type="button"
                className="sa-oht-detail__btn"
                onClick={() => setCoverageConfirm(null)}
                disabled={coverageSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  coverageConfirm === "remove-all"
                    ? "sa-oht-detail__btn sa-oht-detail__btn--danger"
                    : "sa-oht-detail__btn sa-oht-detail__btn--primary"
                }
                onClick={() => {
                  void performCoverageSave();
                }}
                disabled={coverageSaving}
              >
                {coverageSaving
                  ? "Saving..."
                  : coverageConfirm === "remove-all"
                    ? "Remove all coverage"
                    : "Update coverage"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {memberConfirm ? (
        <div
          className="sa-oht-detail__modalOverlay"
          role="presentation"
          onClick={() => {
            if (memberActionBusyId == null) setMemberConfirm(null);
          }}
        >
          <div
            className="sa-oht-detail__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="oht-member-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="oht-member-confirm-title"
              className="sa-oht-detail__modalTitle"
            >
              {memberConfirmTitle(memberConfirm.kind, memberConfirm.member)}
            </h2>
            <p className="sa-oht-detail__modalBody">
              {memberConfirmBody(memberConfirm.kind, memberConfirm.member)}
            </p>
            <div className="sa-oht-detail__btnRow sa-oht-detail__btnRow--end">
              <button
                type="button"
                className="sa-oht-detail__btn"
                onClick={() => setMemberConfirm(null)}
                disabled={memberActionBusyId != null}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  memberConfirm.kind === "deactivate"
                    ? "sa-oht-detail__btn sa-oht-detail__btn--danger"
                    : "sa-oht-detail__btn sa-oht-detail__btn--primary"
                }
                onClick={() => {
                  void confirmMemberAction();
                }}
                disabled={memberActionBusyId != null}
              >
                {memberActionBusyId != null
                  ? memberConfirm.kind === "deactivate"
                    ? "Deactivating..."
                    : "Reactivating..."
                  : memberConfirm.kind === "deactivate"
                    ? "Deactivate"
                    : "Reactivate"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function memberPersonLabel(member: SuperAdminOhtMember) {
  return member.displayName || member.email || "this user";
}

function memberConfirmTitle(
  kind: MemberConfirmKind,
  member: SuperAdminOhtMember
) {
  const role = memberRoleLabel(String(member.roleName));
  const person = memberPersonLabel(member);
  if (kind === "deactivate") {
    return `Deactivate ${role} access?`;
  }
  return `Reactivate ${role} access for ${person}?`;
}

function memberConfirmBody(
  kind: MemberConfirmKind,
  member: SuperAdminOhtMember
) {
  if (kind === "reactivate") {
    return "This restores this OHT assignment only.";
  }
  if (member.roleName === "CommunityNavigator") {
    return "This removes this Community Navigator assignment only. Other roles and other Ontario Health Team assignments will not be changed. If this leaves the OHT with no active Community Navigators, Navigator intake will be disabled automatically.";
  }
  return "This removes this OHT Admin assignment only. The user’s other roles and access to other Ontario Health Teams will not be changed.";
}

function MemberRoleGroup({
  title,
  emptyText,
  members,
  loading,
  busy,
  ohtIsActive,
  actionBusyId,
  resendBusyId,
  showAdd,
  addLabel,
  onAdd,
  onDeactivate,
  onReactivate,
  onResend,
}: {
  title: string;
  emptyText: string;
  members: SuperAdminOhtMember[];
  loading: boolean;
  busy: boolean;
  ohtIsActive: boolean;
  actionBusyId: number | null;
  resendBusyId: number | null;
  showAdd: boolean;
  addLabel: string;
  onAdd: () => void;
  onDeactivate: (member: SuperAdminOhtMember) => void;
  onReactivate: (member: SuperAdminOhtMember) => void;
  onResend: (member: SuperAdminOhtMember) => void;
}) {
  return (
    <div className="sa-oht-detail__memberGroup">
      <div className="sa-oht-detail__cardHeader">
        <h3 className="sa-oht-detail__subTitle">{title}</h3>
        {showAdd ? (
          <button
            type="button"
            className="sa-oht-detail__btn"
            onClick={onAdd}
            disabled={busy || loading}
          >
            {addLabel}
          </button>
        ) : null}
      </div>

      {loading && members.length === 0 ? (
        <p className="sa-oht-detail__muted">Loading members...</p>
      ) : members.length === 0 ? (
        <p className="sa-oht-detail__muted">{emptyText}</p>
      ) : (
        <div className="sa-oht-detail__coverageWrap">
          <table className="sa-oht-detail__table">
            <thead>
              <tr>
                <th className="sa-oht-detail__th" scope="col">
                  Name
                </th>
                <th className="sa-oht-detail__th" scope="col">
                  Email
                </th>
                <th className="sa-oht-detail__th" scope="col">
                  Assignment
                </th>
                <th className="sa-oht-detail__th" scope="col">
                  Account
                </th>
                <th className="sa-oht-detail__th" scope="col">
                  Activated
                </th>
                <th className="sa-oht-detail__th" scope="col">
                  Deactivated
                </th>
                <th className="sa-oht-detail__th" scope="col">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const rowActionBusy = actionBusyId === member.userRoleId;
                const rowResendBusy = resendBusyId === member.userRoleId;
                const rowBusy = rowActionBusy || rowResendBusy;
                const showResend = canResendOhtMemberAccessEmail({
                  member,
                  ohtIsActive,
                });
                return (
                  <tr key={member.userRoleId}>
                    <td className="sa-oht-detail__td">
                      {member.displayName || "—"}
                    </td>
                    <td className="sa-oht-detail__td">{member.email || "—"}</td>
                    <td className="sa-oht-detail__td">
                      <span
                        className={
                          member.isActive
                            ? "sa-oht-detail__status sa-oht-detail__status--active"
                            : "sa-oht-detail__status sa-oht-detail__status--inactive"
                        }
                      >
                        {member.isActive
                          ? "Active assignment"
                          : "Inactive assignment"}
                      </span>
                    </td>
                    <td className="sa-oht-detail__td">
                      <span className="sa-oht-detail__status">
                        {member.userStatus || "—"}
                      </span>
                    </td>
                    <td className="sa-oht-detail__td">
                      {fmtDate(member.activatedAt)}
                    </td>
                    <td className="sa-oht-detail__td">
                      {member.isActive
                        ? "—"
                        : fmtDate(member.deactivatedAt)}
                    </td>
                    <td className="sa-oht-detail__td">
                      <div className="sa-oht-detail__memberActions">
                        {member.isActive ? (
                          <>
                            {showResend ? (
                              <button
                                type="button"
                                className="sa-oht-detail__btn sa-oht-detail__btn--compact"
                                onClick={() => onResend(member)}
                                disabled={busy || rowBusy}
                                aria-busy={rowResendBusy}
                              >
                                {rowResendBusy
                                  ? "Sending..."
                                  : "Resend access email"}
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="sa-oht-detail__btn sa-oht-detail__btn--compact sa-oht-detail__btn--danger"
                              onClick={() => onDeactivate(member)}
                              disabled={busy || rowBusy}
                            >
                              {rowActionBusy ? "Deactivating..." : "Deactivate"}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="sa-oht-detail__btn sa-oht-detail__btn--compact"
                            onClick={() => onReactivate(member)}
                            disabled={busy || rowBusy}
                          >
                            {rowActionBusy ? "Reactivating..." : "Reactivate"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="sa-oht-detail__kpi">
      <div className="sa-oht-detail__kpiLabel">{label}</div>
      <div className="sa-oht-detail__kpiValue">{value}</div>
    </div>
  );
}

function Prereq({
  label,
  pass,
  passText,
  failText,
}: {
  label: string;
  pass: boolean;
  passText: string;
  failText: string;
}) {
  return (
    <li className="sa-oht-detail__prereqItem">
      <span className="sa-oht-detail__prereqLabel">{label}</span>
      <span
        className={
          pass
            ? "sa-oht-detail__status sa-oht-detail__status--pass"
            : "sa-oht-detail__status sa-oht-detail__status--missing"
        }
      >
        {pass ? passText : failText}
      </span>
    </li>
  );
}

function fmtDate(val: string | null | undefined) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}
